import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import {
  ContextualTipId,
  useContextualTip,
  useContextualTipContext,
} from '@resistapp/client/contexts/contextual-tip-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { ComparableSampleGroupType, type SampleTypeGroup } from '@resistapp/common/comparable-sample-groups';
import { ResistanceLevel } from '@resistapp/common/statistics/resistance-index';
import { MetricMode, ProcessMode } from '@resistapp/common/types';
import { isNil } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { DownRightIcon, RightIcon, UpRightIcon } from '../icons/diaconal-arrow-icons';
import { OverviewMetricLabel, getChangeIndicatorColor } from '../shared/overview-metric-label';
import { theme } from '../shared/theme';
import { ContextualTip } from '../tooltips/contextual-tips/contextual-tip';
import { isBeforeOrChangePresent } from './types';

interface Datum {
  level: ResistanceLevel | null | undefined;
  metric: number | null;
  label?: string;
}

interface ResistanceBoxesProps {
  beforeOrChange?: Datum;
  after?: Datum;
  handleSampleTypeGroupChange: (envTypeGroup: SampleTypeGroup) => void;
  groupToSelect?: SampleTypeGroup;
}

export function ResistanceBoxes({
  beforeOrChange,
  after,
  handleSampleTypeGroupChange,
  groupToSelect,
}: ResistanceBoxesProps) {
  const { metricMode, defaultProcessMode, activeAbundanceMode } = useOverviewContext();
  const { setTipEnabled } = useContextualTipContext();

  // Determine highlighting and visibility logic first
  const changeDirection = getChangeDirection(beforeOrChange, after, metricMode);
  const renderBeforeAsWhite = defaultProcessMode === ProcessMode.AFTER && !isNil(after?.metric);
  const renderAfterAsWhite = defaultProcessMode === ProcessMode.BEFORE && !isNil(beforeOrChange?.metric);
  const showBeforeArrow = renderBeforeAsWhite && changeDirection !== undefined;
  const showAfterArrow =
    defaultProcessMode === ProcessMode.BEFORE && after !== undefined && changeDirection !== undefined;
  const beforeLabelShown = isBeforeOrChangePresent(beforeOrChange);
  const afterLabelShown = !!after && after.metric !== null && metricMode !== MetricMode.REDUCTION;

  // Setup contextual tip for secondary (white) metric label
  const beforeLabelRef = useRef<HTMLDivElement>(null);
  const afterLabelRef = useRef<HTMLDivElement>(null);

  const anchorRef = renderBeforeAsWhite ? beforeLabelRef : renderAfterAsWhite ? afterLabelRef : undefined;

  const {
    visible: tipVisible,
    message: tipMessage,
    dismiss: tipDismiss,
    markInteraction: markTipInteraction,
    animationDelay: tipAnimationDelay,
    showDismiss: tipShowDismiss,
  } = useContextualTip(ContextualTipId.OVERVIEW_SECONDARY_METRIC_LABEL, metricMode);

  // Enable or disable the tip based on presence of both labels (process site) and which one is white
  useEffect(() => {
    const enable = beforeLabelShown && afterLabelShown && (renderBeforeAsWhite || renderAfterAsWhite);
    setTipEnabled(ContextualTipId.OVERVIEW_SECONDARY_METRIC_LABEL, enable);
  }, [beforeLabelShown, afterLabelShown, renderBeforeAsWhite, renderAfterAsWhite, setTipEnabled]);

  const onLabelClick = useCallback(
    (envTypeGroup: SampleTypeGroup) => {
      // record contextual tip interaction if relevant
      if (renderBeforeAsWhite || renderAfterAsWhite) {
        markTipInteraction();
      }
      if (afterLabelShown) {
        handleSampleTypeGroupChange(envTypeGroup);
      } else if (groupToSelect) {
        handleSampleTypeGroupChange(groupToSelect);
      }
    },
    [
      afterLabelShown,
      groupToSelect,
      handleSampleTypeGroupChange,
      markTipInteraction,
      renderBeforeAsWhite,
      renderAfterAsWhite,
    ],
  );

  return (
    <Flex gap={theme.spacing[4]} position="relative">
      {(beforeLabelShown || !afterLabelShown) && (
        <Flex
          flexDirection="column"
          width="100px"
          ref={renderBeforeAsWhite ? beforeLabelRef : null}
          onClick={() => {
            onLabelClick(ComparableSampleGroupType.RAW_WASTEWATER);
          }}
          style={{ cursor: afterLabelShown || !beforeOrChange?.metric ? 'pointer' : 'default' }}
        >
          <OverviewMetricLabel
            id="influentLevelContainer"
            level={beforeOrChange?.level ?? null}
            metricMode={metricMode}
            metric={beforeOrChange?.metric ?? null}
            renderAsWhite={renderBeforeAsWhite}
            style={{
              position: 'relative',
            }}
            activeAbundanceMode={activeAbundanceMode}
            showAlways={!afterLabelShown}
          >
            {showBeforeArrow && (
              <ArrowContainer right>
                {changeDirection < 0 ? (
                  <DownRightIcon style={{ color: getChangeIndicatorColor(-1) + 'A0' }} />
                ) : changeDirection > 0 ? (
                  <UpRightIcon style={{ color: getChangeIndicatorColor(1) + 'A0' }} />
                ) : (
                  <RightIcon style={{ color: getChangeIndicatorColor(0) + 'A0' }} />
                )}
              </ArrowContainer>
            )}
          </OverviewMetricLabel>
          {beforeOrChange?.label && <FluentsTextContainer>{beforeOrChange.label}</FluentsTextContainer>}
        </Flex>
      )}
      {afterLabelShown && (
        <Flex
          flexDirection="column"
          width="100px"
          ref={renderAfterAsWhite ? afterLabelRef : null}
          onClick={() => {
            onLabelClick(ComparableSampleGroupType.TREATED_WASTEWATER);
          }}
          style={{ cursor: beforeLabelShown || !after.metric ? 'pointer' : 'default' }}
        >
          <OverviewMetricLabel
            id="effluentLevelContainer"
            level={after.level ?? null}
            metricMode={metricMode}
            metric={after.metric}
            renderAsWhite={renderAfterAsWhite}
            activeAbundanceMode={activeAbundanceMode}
            showAlways={!beforeLabelShown}
          >
            {showAfterArrow && (
              <ArrowContainer>
                {changeDirection < 0 ? (
                  <DownRightIcon style={{ color: getChangeIndicatorColor(-1) + 'A0' }} />
                ) : changeDirection > 0 ? (
                  <UpRightIcon style={{ color: getChangeIndicatorColor(1) + 'A0' }} />
                ) : (
                  <RightIcon style={{ color: getChangeIndicatorColor(0) + 'A0' }} />
                )}
              </ArrowContainer>
            )}
          </OverviewMetricLabel>
          {after.label && <FluentsTextContainer>{after.label}</FluentsTextContainer>}
        </Flex>
      )}

      {/* Contextual tip */}
      {tipVisible && anchorRef && (
        <ContextualTip
          anchorRef={anchorRef as React.RefObject<HTMLElement>}
          message={tipMessage || ''}
          onDismiss={tipDismiss}
          placement="top"
          animationDelay={tipAnimationDelay}
          showDismiss={tipShowDismiss}
        />
      )}
    </Flex>
  );
}

function getChangeDirection(
  firstLevel: Datum | undefined,
  secondLevel: Datum | undefined,
  metricMode: MetricMode,
): -1 | 1 | 0 | undefined {
  const roundedBeforeMetric = !isNil(firstLevel?.metric)
    ? Number(firstLevel.metric.toFixed(metricMode === MetricMode.ARGI || metricMode === MetricMode.REDUCTION ? 1 : 0))
    : null;
  const roundedAfterMetric = !isNil(secondLevel?.metric)
    ? Number(secondLevel.metric.toFixed(metricMode === MetricMode.ARGI || metricMode === MetricMode.REDUCTION ? 1 : 0))
    : null;
  return isNil(roundedAfterMetric) || isNil(roundedBeforeMetric) || metricMode === MetricMode.REDUCTION
    ? undefined
    : roundedAfterMetric === roundedBeforeMetric
      ? 0
      : roundedAfterMetric > roundedBeforeMetric
        ? 1
        : -1;
}

const FluentsTextContainer = styled.div`
  color: ${theme.colors.neutral500};
  text-align: center;
  font-size: ${theme.fontSize.extraSmall};
  font-weight: ${theme.fontWeight.heavy};
`;

const ArrowContainer = styled.div<{ right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => (props.right ? 'right: 10px;' : 'left: 8px;')}
  display: flex;
  justify-content: center;
  align-items: center;
  width: 23px;
  height: 23px;
`;
